import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import { LoggerService } from '../../services/LoggerService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, user, isLoading, accessToken, logout } = useAuthStore();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  // Validate token with backend on mount
  useEffect(() => {
    const validateToken = async () => {
      // If no token, user, or not authenticated, immediately invalidate
      if (!accessToken || !user || !isAuthenticated) {
        setIsValidating(false);
        setIsValid(false);
        // Clear any stale auth state
        if (isAuthenticated && !accessToken) {
          logout();
        }
        return;
      }

      try {
        // Validate token by calling /auth/me endpoint
        const currentUser = await authService.getCurrentUser(accessToken);
        
        // If we get a user back, token is valid
        if (currentUser && currentUser.id) {
          setIsValid(true);
        } else {
          // Invalid token, logout
          logout();
          setIsValid(false);
        }
      } catch (error) {
        // Token validation failed, logout
        LoggerService.error('Token validation failed:', error);
        logout();
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [accessToken, isAuthenticated, user, logout]);

  // IMMEDIATELY block if no token or user - don't wait for validation
  // This prevents any access without proper authentication
  if (!accessToken || !user || !isAuthenticated) {
    LoggerService.debug('[ProtectedRoute] Blocking access - missing token, user, or authentication', {
      hasToken: !!accessToken,
      hasUser: !!user,
      isAuthenticated
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading spinner while validating token
  if (isLoading || isValidating) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if token validation failed
  if (!isValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user is active
  if (!user.is_active) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
